import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Users, Package, RefreshCw, ChevronRight, Search,
  ArrowUpRight, CheckCircle2, AlertCircle, Clock, Loader2, Shield,
  Database, TrendingUp, ChevronDown, ChevronUp, X
} from 'lucide-react';
import api from '@/services/api';

const TIER_ORDER = ['Lite', 'Express_Retail', 'RetailCLIQ_Lite', 'Standard', 'Professional', 'Enterprise_Unlimited'];
const TIER_COLORS = {
  Lite: { bg: '#f1f5f9', text: '#64748b', badge: 'bg-slate-100 text-slate-600' },
  Express_Retail: { bg: '#eff6ff', text: '#2563eb', badge: 'bg-blue-50 text-blue-600' },
  RetailCLIQ_Lite: { bg: '#f0fdf4', text: '#16a34a', badge: 'bg-green-50 text-green-600' },
  Standard: { bg: '#fefce8', text: '#ca8a04', badge: 'bg-yellow-50 text-yellow-700' },
  Professional: { bg: '#fdf4ff', text: '#9333ea', badge: 'bg-purple-50 text-purple-600' },
  Enterprise_Unlimited: { bg: '#fff7ed', text: '#ea580c', badge: 'bg-orange-50 text-orange-600' },
};
const getTierColor = (tier) => TIER_COLORS[tier] || { bg: '#f8faff', text: '#1e293b', badge: 'bg-slate-100 text-slate-700' };

// ── Tier Upgrade Path ─────────────────────────────────────────────────────────
function TierPathRow({ currentTier, industry }) {
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!industry) return;
    setLoading(true);
    api.get(`/api/admin/tier-path?industry=${encodeURIComponent(industry)}`)
      .then(r => r.data?.data || [])
      .catch(() => [])
      .then(d => { setPath(d); setLoading(false); });
  }, [industry]);

  if (loading) return <div style={{ padding: '6px 0', fontSize: 9, color: '#94a3b8' }}>Loading tier path...</div>;
  if (!path.length) return null;

  const sorted = [...path].sort((a, b) => (TIER_ORDER.indexOf(a.TierCode) - TIER_ORDER.indexOf(b.TierCode)));

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '8px 0' }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: '#64748b', marginRight: 4 }}>TIER PATH:</span>
      {sorted.map((t, idx) => {
        const tc = getTierColor(t.TierCode);
        const isCurrent = t.TierCode === currentTier;
        return (
          <React.Fragment key={t.TierCode}>
            <div style={{
              display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
              padding: '4px 10px', borderRadius: 8, fontSize: 8,
              background: isCurrent ? tc.bg : '#f8faff',
              border: `1.5px solid ${isCurrent ? tc.text : '#e2e8f0'}`,
              color: isCurrent ? tc.text : '#94a3b8', fontWeight: isCurrent ? 700 : 400,
              minWidth: 80, textAlign: 'center'
            }}>
              <span style={{ fontWeight: 700, fontSize: 9 }}>{t.TierCode.replace(/_/g, ' ')}</span>
              <span>{t.moduleCount} mod · {t.screenCount} screens</span>
              {isCurrent && <span style={{ marginTop: 2, fontSize: 7, background: tc.text, color: '#fff', borderRadius: 3, padding: '0 4px' }}>CURRENT</span>}
            </div>
            {idx < sorted.length - 1 && <ChevronRight size={12} color="#cbd5e1" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Single Tenant Row ─────────────────────────────────────────────────────────
function TenantRow({ tenant, isDark, onRefreshUsers, onSyncModules }) {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [syncingModules, setSyncingModules] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [moduleResult, setModuleResult] = useState(null);
  const [userResult, setUserResult] = useState(null);

  const tc = getTierColor(tenant.TierCode);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    api.get(`/api/admin/tenant-users?clientId=${tenant.ClientId}`)
      .then(r => { setUsers(r.data?.data || []); })
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));
  }, [tenant.ClientId]);

  useEffect(() => {
    if (expanded && !users) loadUsers();
  }, [expanded, users, loadUsers]);

  const handleSyncUsers = async () => {
    setSyncingUsers(true); setUserResult(null);
    try {
      const r = await api.post(`/api/admin/sync-users/${tenant.ClientId}`);
      setUserResult({ ok: true, msg: r.data.message });
      loadUsers();
      onRefreshUsers?.();
    } catch (e) {
      setUserResult({ ok: false, msg: e.response?.data?.error || 'Sync failed' });
    } finally { setSyncingUsers(false); }
  };

  const handleSyncModules = async () => {
    setSyncingModules(true); setModuleResult(null);
    try {
      const r = await api.post(`/api/admin/sync-modules/${tenant.ClientId}`);
      setModuleResult({ ok: true, msg: r.data.message, synced: r.data.synced });
      onSyncModules?.();  // triggers parent refresh so SyncedModuleCount updates in list
    } catch (e) {
      setModuleResult({ ok: false, msg: e.response?.data?.error || 'Module sync failed' });
    } finally { setSyncingModules(false); }
  };

  const statusColor = tenant.CStatus === 'A' ? '#16a34a' : '#dc2626';
  const lastSync = tenant.LastUserSyncAt ? new Date(tenant.LastUserSyncAt).toLocaleDateString() : 'Never';
  const moduleSynced = tenant.ModulesSyncedAt ? new Date(tenant.ModulesSyncedAt).toLocaleDateString() : null;

  return (
    <div style={{
      border: `1px solid ${expanded ? '#bfdbfe' : (isDark ? '#1e293b' : '#e2e8f0')}`,
      borderRadius: 8, marginBottom: 8, overflow: 'hidden', transition: 'border-color .2s',
      background: isDark ? '#0f172a' : '#fff'
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}>
        <Building2 size={14} color={isDark ? '#94a3b8' : '#64748b'} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {tenant.CompName || tenant.ClientId}
          </div>
          <div style={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8' }}>
            {tenant.ClientId} · {tenant.SQLDBName || 'No DB'}
          </div>
        </div>
        {/* Tier badge */}
        <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 6, background: tc.bg, color: tc.text, fontWeight: 700, flexShrink: 0 }}>
          {(tenant.TierCode || 'Unknown').replace(/_/g, ' ')}
        </span>
        {/* Industry */}
        <span style={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8', flexShrink: 0 }}>{tenant.IndustryCode}</span>
        {/* Status dot */}
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0 }} title={tenant.CStatus === 'A' ? 'Active' : 'Inactive'} />
        {/* Users synced */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <Users size={10} color={isDark ? '#64748b' : '#94a3b8'} />
          <span style={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8' }}>{tenant.SyncedUsers || 0}</span>
        </div>
        {/* Module sync status */}
        {moduleSynced
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              <CheckCircle2 size={12} color="#16a34a" title={`Modules synced: ${moduleSynced}`} />
              <span style={{ fontSize: 9, color: '#16a34a', fontWeight: 600 }}>{tenant.SyncedModuleCount || 0} mod</span>
            </div>
          : <AlertCircle size={12} color="#f59e0b" title="Modules not yet synced" />
        }
        {expanded ? <ChevronUp size={14} color={isDark ? '#64748b' : '#94a3b8'} /> : <ChevronDown size={14} color={isDark ? '#64748b' : '#94a3b8'} />}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`, padding: '12px 14px' }}>
          {/* Tier path */}
          <TierPathRow currentTier={tenant.TierCode} industry={tenant.IndustryCode} />

          {/* Two-column actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
            {/* User sync panel */}
            <div style={{ border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 6, padding: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Users size={11} /> Users
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={loadUsers} title="Refresh list"
                    style={{ fontSize: 8, padding: '2px 6px', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 3, cursor: 'pointer', background: 'transparent', color: isDark ? '#64748b' : '#94a3b8' }}>
                    <RefreshCw size={9} />
                  </button>
                  <button onClick={handleSyncUsers} disabled={syncingUsers}
                    data-testid={`sync-users-btn-${tenant.ClientId}`}
                    style={{ fontSize: 8, padding: '2px 8px', border: 'none', borderRadius: 3, cursor: syncingUsers ? 'default' : 'pointer', background: '#2563eb', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                    {syncingUsers ? <Loader2 size={8} className="animate-spin" /> : <ArrowUpRight size={8} />} Sync to Master
                  </button>
                </div>
              </div>
              {userResult && (
                <div style={{ fontSize: 8, padding: '3px 6px', borderRadius: 3, marginBottom: 6, background: userResult.ok ? '#f0fdf4' : '#fef2f2', color: userResult.ok ? '#16a34a' : '#dc2626' }}>
                  {userResult.msg}
                </div>
              )}
              <div style={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6 }}>
                Last sync: {lastSync} · {tenant.SyncedUsers || 0} synced in MasterDB
              </div>
              {usersLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}><Loader2 size={14} className="animate-spin" style={{ color: '#94a3b8' }} /></div>
              ) : users?.length ? (
                <div style={{ maxHeight: 120, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                    <thead>
                      <tr style={{ background: isDark ? '#1e293b' : '#f8faff' }}>
                        {['ID', 'Name', 'Email', 'Status', 'Last Sync'].map(h => (
                          <th key={h} style={{ padding: '2px 6px', textAlign: 'left', color: isDark ? '#64748b' : '#94a3b8', fontWeight: 600, fontSize: 8 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.SyncId} style={{ borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` }}>
                          <td style={{ padding: '2px 6px', color: isDark ? '#64748b' : '#94a3b8' }}>{u.TenantUserId}</td>
                          <td style={{ padding: '2px 6px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{u.UserName}</td>
                          <td style={{ padding: '2px 6px', color: isDark ? '#64748b' : '#94a3b8' }}>{u.Email || '—'}</td>
                          <td style={{ padding: '2px 6px' }}>
                            <span style={{ fontSize: 7, padding: '0 4px', borderRadius: 3, background: u.Status === 'S' ? '#f0fdf4' : '#fef2f2', color: u.Status === 'S' ? '#16a34a' : '#dc2626' }}>
                              {u.Status === 'S' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '2px 6px', color: isDark ? '#64748b' : '#94a3b8', fontSize: 8 }}>{u.LastSyncAt ? new Date(u.LastSyncAt).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center', padding: '8px 0' }}>No synced users. Click "Sync to Master" to pull users.</div>
              )}
            </div>

            {/* Module sync panel */}
            <div style={{ border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 6, padding: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Package size={11} /> Module Sync
                </span>
                <button onClick={handleSyncModules} disabled={syncingModules}
                  data-testid={`sync-modules-btn-${tenant.ClientId}`}
                  style={{ fontSize: 8, padding: '2px 8px', border: 'none', borderRadius: 3, cursor: syncingModules ? 'default' : 'pointer', background: '#16a34a', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                  {syncingModules ? <Loader2 size={8} className="animate-spin" /> : <Database size={8} />} Push Modules
                </button>
              </div>
              {moduleResult && (
                <div style={{ fontSize: 8, padding: '3px 6px', borderRadius: 3, marginBottom: 6, background: moduleResult.ok ? '#f0fdf4' : '#fef2f2', color: moduleResult.ok ? '#16a34a' : '#dc2626' }}>
                  {moduleResult.msg}
                </div>
              )}
              <div style={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 8 }}>
                {moduleSynced
                  ? <><CheckCircle2 size={9} style={{ display: 'inline', color: '#16a34a', marginRight: 3 }} />Last synced: {moduleSynced} &nbsp;·&nbsp; <strong>{tenant.SyncedModuleCount || 0} modules</strong> pushed</>
                  : <><AlertCircle size={9} style={{ display: 'inline', color: '#f59e0b', marginRight: 3 }} />Not yet synced</>
                }
              </div>
              <div style={{ fontSize: 9, color: isDark ? '#475569' : '#64748b', lineHeight: 1.7 }}>
                <div>• Pushes all modules from reference DB to tenant</div>
                <div>• Tier <strong>{(tenant.TierCode || '').replace(/_/g, ' ')}</strong> controls visibility client-side</div>
                <div>• Tier upgrade? Re-sync after changing tier in ClientMaster</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Tenant Hub Tab ────────────────────────────────────────────────────────
export function TenantHubTab({ isDark }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState(null);

  useEffect(() => {
    setLoading(true); setError('');
    api.get(`/api/admin/tenants${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      .then(r => setTenants(r.data?.data || []))
      .catch(e => setError(e.response?.data?.error || 'Failed to load tenants'))
      .finally(() => setLoading(false));
  }, [search, refreshKey]);

  const handleSyncAll = async () => {
    if (syncingAll || !tenants.length) return;
    const results = {};
    setSyncingAll(true);
    setSyncAllProgress({ current: 0, total: tenants.length, results });
    for (let i = 0; i < tenants.length; i++) {
      const t = tenants[i];
      results[t.ClientId] = { status: 'running', name: t.CompName || t.ClientId };
      setSyncAllProgress({ current: i, total: tenants.length, results: { ...results } });
      try {
        const r = await api.post(`/api/admin/sync-modules/${t.ClientId}`);
        results[t.ClientId] = { status: 'done', synced: r.data?.synced || 0, name: t.CompName || t.ClientId };
      } catch (e) {
        results[t.ClientId] = { status: 'error', msg: e.response?.data?.error || 'Failed', name: t.CompName || t.ClientId };
      }
      setSyncAllProgress({ current: i + 1, total: tenants.length, results: { ...results } });
    }
    setSyncingAll(false);
    setRefreshKey(k => k + 1);
  };

  const activeTenants = tenants.filter(t => t.CStatus === 'A');
  const totalUsers = tenants.reduce((s, t) => s + (t.SyncedUsers || 0), 0);
  const syncedModules = tenants.filter(t => t.ModulesSyncedAt).length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Shield size={16} color="#2563eb" />
          <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b' }}>Tenant Hub</span>
          <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 12, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>SUPER_ADMIN ONLY</span>
        </div>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          {[
            { label: 'Total Tenants', value: tenants.length, icon: <Building2 size={12} />, color: '#2563eb' },
            { label: 'Active', value: activeTenants.length, icon: <CheckCircle2 size={12} />, color: '#16a34a' },
            { label: 'Users Synced', value: totalUsers, icon: <Users size={12} />, color: '#9333ea' },
            { label: 'Modules Pushed', value: `${syncedModules}/${tenants.length}`, icon: <Package size={12} />, color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: isDark ? '#0f172a' : '#f8faff', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
              <span style={{ color: s.color }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: isDark ? '#f1f5f9' : '#1e293b', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 8, color: isDark ? '#64748b' : '#94a3b8', fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              onClick={handleSyncAll} disabled={syncingAll || loading} title="Sync modules for ALL tenants"
              data-testid="sync-all-modules-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, padding: '4px 10px', border: `1px solid ${isDark ? '#1e40af' : '#3b82f6'}`, borderRadius: 5, cursor: syncingAll ? 'not-allowed' : 'pointer', background: isDark ? 'rgba(37,99,235,0.15)' : '#eff6ff', color: syncingAll ? '#94a3b8' : '#2563eb', fontWeight: 600 }}>
              {syncingAll ? <Loader2 size={10} className="animate-spin" /> : <Package size={10} />}
              {syncingAll ? `${syncAllProgress?.current ?? 0}/${syncAllProgress?.total ?? 0}…` : 'Sync All'}
            </button>
            <button onClick={() => setRefreshKey(k => k + 1)} title="Refresh"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, padding: '4px 10px', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 5, cursor: 'pointer', background: 'transparent', color: isDark ? '#64748b' : '#94a3b8' }}>
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by company name or client ID…"
            data-testid="tenant-search-input"
            style={{ width: '100%', padding: '7px 8px 7px 26px', fontSize: 11, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 5, background: isDark ? '#1e293b' : '#fff', color: isDark ? '#f1f5f9' : '#1e293b', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {/* Sync All Progress */}
      {syncAllProgress && (
        <div style={{ margin: '8px 20px 0', padding: '10px 14px', background: isDark ? '#0f172a' : '#f8faff', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#475569' }}>
              {syncingAll ? `Syncing modules… ${syncAllProgress.current}/${syncAllProgress.total}` : `Sync Complete — ${syncAllProgress.total} tenants`}
            </span>
            {!syncingAll && (
              <button onClick={() => setSyncAllProgress(null)} style={{ fontSize: 9, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 2px' }}>Dismiss</button>
            )}
          </div>
          {/* Progress bar */}
          <div style={{ height: 4, borderRadius: 2, background: isDark ? '#1e293b' : '#e2e8f0', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', background: syncingAll ? '#3b82f6' : '#16a34a', width: `${Math.round((syncAllProgress.current / syncAllProgress.total) * 100)}%`, transition: 'width 0.3s ease' }} />
          </div>
          {/* Per-tenant result chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(syncAllProgress.results).map(([cid, r]) => (
              <span key={cid} data-testid={`sync-all-result-${cid}`}
                style={{ fontSize: 8, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                  background: r.status === 'done' ? '#dcfce7' : r.status === 'error' ? '#fef2f2' : '#eff6ff',
                  color: r.status === 'done' ? '#15803d' : r.status === 'error' ? '#dc2626' : '#2563eb',
                  border: `1px solid ${r.status === 'done' ? '#bbf7d0' : r.status === 'error' ? '#fecaca' : '#bfdbfe'}` }}>
                {r.status === 'running' && <Loader2 size={7} className="animate-spin" style={{ display:'inline', marginRight:3 }} />}
                {r.name}: {r.status === 'done' ? `${r.synced} mod` : r.status === 'error' ? `err` : '…'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tenant list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <Loader2 size={24} className="animate-spin" style={{ color: '#2563eb' }} />
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#dc2626', fontSize: 12 }}>
            <AlertCircle size={20} style={{ marginBottom: 8 }} /> {error}
          </div>
        ) : !tenants.length ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 12 }}>
            No tenants found.
          </div>
        ) : (
          tenants.map(t => (
            <TenantRow key={t.ClientId} tenant={t} isDark={isDark}
              onRefreshUsers={() => setRefreshKey(k => k + 1)}
              onSyncModules={() => setRefreshKey(k => k + 1)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default TenantHubTab;
